import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { generateDummyShifts } from '../utils/shiftUtils';

// API response interface for type safety
export interface ApiResponse<T = any> {
  data: T;
  message?: string;
  success: boolean;
}

// Error interface
export interface ApiError {
  message: string;
  statusCode: number;
  errors?: Record<string, string[]>;
}

// Create axios instance with defaults
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL ||
    (process.env.NODE_ENV === 'development'
      ? 'http://localhost:4000/api'
      : 'https://api.medibridge.co.kr/api'),
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds
  withCredentials: false, // Disable credentials to avoid CORS issues
});

// Flag to use mock data instead of real API
const USE_MOCK_DATA = false;

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Get token from localStorage
    const token = localStorage.getItem('token');
    
    if (process.env.NODE_ENV === 'development') {
      console.log('API Request:', config.url);
      console.log('Token from localStorage:', token ? 'Token exists' : 'No token');
    }

    // If token exists, add it to the headers
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      if (process.env.NODE_ENV === 'development') {
        console.log('Auth header set:', `Bearer ${token.substring(0, 10)}...`);
      }
    } else {
      if (process.env.NODE_ENV === 'development') {
        console.log('No auth header set - missing token');
      }
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const customError: ApiError = {
      message: 'An unexpected error occurred',
      statusCode: 500,
    };

    // Handle network errors
    if (!error.response) {
      customError.message = 'Network error - please check your connection';
      return Promise.reject(customError);
    }

    // Handle API error responses
    const { status, data } = error.response as AxiosResponse;

    customError.statusCode = status;

    // Handle different status codes
    switch (status) {
      case 400:
        customError.message = 'Bad request';
        if (data.errors) {
          customError.errors = data.errors;
        }
        break;
      case 401:
        customError.message = '인증 오류 - 다시 로그인해주세요';
        
        // 토큰 만료인 경우에만 이벤트 발생
        // 'expired', 'invalid token', 'jwt expired' 등의 메시지 확인
        const errorMsg = data?.message?.toLowerCase() || '';
        if (errorMsg.includes('expired') || errorMsg.includes('invalid token')) {
          // 토큰 만료 또는 유효하지 않은 토큰인 경우에만 로그아웃 이벤트 발생
          window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        }
        break;
      case 403:
        customError.message = '접근 권한이 없습니다';
        break;
      case 404:
        customError.message = '요청한 리소스를 찾을 수 없습니다';
        break;
      case 422:
        customError.message = '유효성 검증 오류';
        if (data.errors) {
          customError.errors = data.errors;
        }
        break;
      case 500:
        customError.message = '서버 오류가 발생했습니다';
        break;
      default:
        customError.message = '오류가 발생했습니다';
    }

    // Use the error message from the response if available
    if (data && data.message) {
      customError.message = data.message;
    }

    return Promise.reject(customError);
  }
);

// Mock data helper function
const getMockResponse = (url: string, config?: AxiosRequestConfig) => {
  // Extract endpoint from URL
  const endpoint = url.split('/').pop();
  const hospitalId = url.includes('/hospital/') ? url.split('/hospital/')[1].split('/')[0] : null;
  
  if (url.includes('/shifts') && hospitalId) {
    // Generate dummy shifts for this hospital
    const startDate = new Date();
    const endDate = new Date();
    endDate.setMonth(endDate.getMonth() + 1); // Reduced to 1 month
    
    // Use count-based dummy shifts with options, reduced to 20 shifts
    const dummyShifts = generateDummyShifts(20, {
      hospitalId: hospitalId || 'test-hospital',
      startDate: startDate,
      endDate: endDate
    });
    
    return {
      data: { shifts: dummyShifts, total: dummyShifts.length },
      message: 'Mock data fetched successfully',
      success: true
    };
  }
  
  // Default mock response
  return {
    data: {},
    message: 'Mock response',
    success: true
  };
};

// Generic API functions
export const api = {
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    if (USE_MOCK_DATA) {
      return getMockResponse(url, config) as ApiResponse<T>;
    }
    
    try {
      const response = await apiClient.get(url, config);
      return {
        data: response.data.data || response.data,
        message: response.data.message || 'Success',
        success: true,
      };
    } catch (error) {
      throw error;
    }
  },

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    if (USE_MOCK_DATA) {
      return {
        data: data as T,
        message: 'Created successfully (mock)',
        success: true,
      };
    }
    
    try {
      const response = await apiClient.post(url, data, config);
      return {
        data: response.data.data || response.data,
        message: response.data.message || 'Created successfully',
        success: true,
      };
    } catch (error) {
      throw error;
    }
  },

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    if (USE_MOCK_DATA) {
      return {
        data: data as T,
        message: 'Updated successfully (mock)',
        success: true,
      };
    }
    
    try {
      const response = await apiClient.put(url, data, config);
      return {
        data: response.data.data || response.data,
        message: response.data.message || 'Updated successfully',
        success: true,
      };
    } catch (error) {
      throw error;
    }
  },

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    if (USE_MOCK_DATA) {
      return {
        data: data as T,
        message: 'Updated successfully (mock)',
        success: true,
      };
    }
    
    try {
      const response = await apiClient.patch(url, data, config);
      return {
        data: response.data.data || response.data,
        message: response.data.message || 'Updated successfully',
        success: true,
      };
    } catch (error) {
      throw error;
    }
  },

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    if (USE_MOCK_DATA) {
      return {
        data: {} as T,
        message: 'Deleted successfully (mock)',
        success: true,
      };
    }
    
    try {
      const response = await apiClient.delete(url, config);
      return {
        data: response.data.data || response.data,
        message: response.data.message || 'Deleted successfully',
        success: true,
      };
    } catch (error) {
      throw error;
    }
  },
};

export default api; 